import {
  Form,
  minValue,
  required,
  useNotify,
  useTranslate,
  useUpdate,
} from "ra-core";
import { useMemo } from "react";
import type { FieldValues } from "react-hook-form";

import { DateInput } from "@/components/admin/date-input";
import { SaveButton } from "@/components/admin/form";
import { NumberInput } from "@/components/admin/number-input";
import { SelectInput } from "@/components/admin/select-input";
import { TextInput } from "@/components/admin/text-input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { toDateOnly } from "../../providers/commons/financeSchedule";
import type { FinanceCharge } from "../../types";
import {
  dayToTimestamp,
  toNullableNumber,
  toNullableText,
} from "../formValues";
import { useFinanceErrorHandler } from "../useFinanceErrorHandler";
import { useFormatMoney } from "../useFormatMoney";
import { paymentMethodChoices } from "../financeOptions";
import { ChargeFileInputs } from "./ChargeInputs";

export interface PaymentDialogProps {
  charge: FinanceCharge | null;
  onClose: () => void;
}

/** Registers the payment of a charge, with its NF and receipt */
export const PaymentDialog = ({ charge, onClose }: PaymentDialogProps) => {
  const translate = useTranslate();
  const notify = useNotify();
  const onError = useFinanceErrorHandler();
  const formatMoney = useFormatMoney();
  const [update] = useUpdate();
  const record = useMemo(
    () =>
      charge && {
        paid_on: toDateOnly(new Date()),
        paid_amount: charge.amount,
        payment_method: charge.payment_method ?? "pix",
        invoice_number: charge.invoice_number ?? "",
        invoice_files: charge.invoice_files ?? [],
        receipt_files: charge.receipt_files ?? [],
      },
    [charge],
  );

  const handleSubmit = async (values: FieldValues) => {
    if (!charge) return;
    await update(
      "finance_charges",
      {
        id: charge.id,
        data: {
          paid_at: dayToTimestamp(values.paid_on),
          paid_amount: toNullableNumber(values.paid_amount),
          payment_method: values.payment_method || null,
          invoice_number: toNullableText(values.invoice_number),
          invoice_files: values.invoice_files ?? [],
          receipt_files: values.receipt_files ?? [],
        },
        previousData: charge,
      },
      {
        onSuccess: () => {
          notify("crm.finance.charges.payment_registered");
          onClose();
        },
        onError,
      },
    );
  };

  return (
    <Dialog
      open={charge != null}
      onOpenChange={(isOpen) => !isOpen && onClose()}
    >
      <DialogContent className="max-h-9/10 overflow-y-auto lg:max-w-xl">
        {charge && record && (
          <Form
            record={record}
            onSubmit={handleSubmit}
            className="flex flex-col gap-4"
          >
            <DialogHeader>
              <DialogTitle>
                {translate("crm.finance.charges.register_payment")}
              </DialogTitle>
              <DialogDescription>
                {charge.description} · {formatMoney(charge.amount)}
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <DateInput
                source="paid_on"
                label="crm.finance.fields.paid_at"
                validate={required()}
                helperText={false}
              />
              <NumberInput
                source="paid_amount"
                label="crm.finance.charges.fields.paid_amount"
                min={0}
                step={0.01}
                validate={[required(), minValue(0)]}
                helperText={false}
              />
              <SelectInput
                source="payment_method"
                label="crm.finance.charges.fields.payment_method"
                choices={paymentMethodChoices}
                helperText={false}
              />
              <TextInput
                source="invoice_number"
                label="crm.finance.charges.fields.invoice_number"
                helperText={false}
              />
            </div>
            <ChargeFileInputs />
            <DialogFooter>
              <SaveButton label="crm.finance.charges.register_payment" />
            </DialogFooter>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
};
